import { Button } from "@faire/slate/components/button";
import React, { useState } from "react";

import { cn } from "../../lib/utils";
import Footer from "../Footer";

import AskAssistantPill from "./AskAssistantPill";
import CompareProducts from "./CompareProducts";
import RetailerTopNav from "./RetailerTopNav";

/**
 * Retailer product details page (current production).
 *
 * A faithful port of the Faire retailer PDP: global top nav, sticky brand
 * header, image mosaic, product details rail (with the inline "Your Assistant"
 * entry), "Shop more from <brand>", ratings & reviews, and the Compare products
 * section. Composed entirely from the shared pattern components so it can be
 * reused as the canvas for PDP experiences (e.g. with the assistant docked).
 */

/**
 * The product identity the PDP renders. Everything else on the page (reviews,
 * description, "Shop more" tiles, shipping) stays fixed — swapping those too
 * would mean templating the whole 900-line port.
 */
export interface RetailerPdpProductContent {
  brandName: string;
  brandAvatarUrl: string;
  /** Mosaic images, largest first. The `stack` layout uses the first two. */
  images: string[];
  name: string;
  priceLabel: string;
  msrpLabel: string;
  caseSizeLabel: string;
  /** Value shown in the quantity stepper, e.g. "8". */
  quantityLabel: string;
  cartTotalLabel: string;
  /**
   * Replaces the hardcoded Nopalera description copy. Omitted keeps the
   * original soap description every existing prototype renders.
   */
  description?: string;
}

const BRAND_AVATAR =
  "https://cdn.faire.com/fastly/22eac98db5671bd758bb622678729186714f51880eee4ff2e81f5d64f3f20dbf.png?dpr=2&fit=crop&format=jpg&height=80&width=80";

const PRODUCT_IMAGES = [
  "https://cdn.faire.com/fastly/c0d94ced91be797cae35e175da1fe68e55c23ac3336ae0b63e1950899c17cc3d.jpeg?format=jpg&precrop=2531%2C2531%2Cx333%2Cy256%2Csafe&width=1440",
  "https://cdn.faire.com/fastly/08ab8b096093096bbdb2ddcd2b9d82ef0bb955835baaa0a888ca34f7a1a6d01c.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
  "https://cdn.faire.com/fastly/1ff5d4eb200d249182b5878b8f9d3788d80086a61a32c6a790319b18f8b4bf86.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
  "https://cdn.faire.com/fastly/1f794cff323926235d8a20b6baf2d272eba48deb5ed4791e49fe62c6dce89e2f.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
];

const ASSISTANT_PROMPTS = [
  "What's the margin on this product?",
  "What's unique about this brand?",
  "How long until it ships?",
];

// Faithful port of the production "Ask your assistant" pill row markup.
const PROMPT_PILL_CLASS =
  "focusable interactive type-fs-paragraph text-fs-action-text-default border-(--slate-color-program-border-ai_suggestions) hover:border-(--pdp-assistant-pill-hover-border) hover:bg-(--slate-color-action-surface-inverse-hover) hover:text-fs-action-text-hover box-border flex h-10 min-w-0 max-w-full items-center justify-start gap-2 overflow-hidden whitespace-nowrap rounded-full border bg-fs-surface-primary px-(--slate-dimensions-4) transition-colors";

interface ShopProduct {
  image: string;
  price: string;
  msrp: string;
  name: string;
  caseSize: string;
  badge?: string;
}

const SHOP_MORE_PRODUCTS: ShopProduct[] = [
  {
    image:
      "https://cdn.faire.com/fastly/03e357386ad84837697fb18c713fd9450f20a0aceb99c702cc10dc831675ddb2.jpeg?format=jpg&precrop=1:1&width=720",
    price: "$15",
    msrp: "MSRP $30",
    name: "Original Moisturizing Botanical Bar",
    caseSize: "Case of 8",
  },
  {
    image:
      "https://cdn.faire.com/fastly/7ed3b065c98751a27666b92aa10cab621e9c751b045feb2f132a6ff27e1b8375.jpeg?format=jpg&precrop=2451%2C2451%2Cx400%2Cy338%2Csafe&width=720",
    price: "$16",
    msrp: "MSRP $32",
    name: "Hibiscus Cactus Body Polish",
    caseSize: "Case of 8",
  },
  {
    image:
      "https://cdn.faire.com/fastly/1618166dde38754f57692881a20b111002a6f9fae9cc8e93ebd58c08448351a2.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
    price: "$39",
    msrp: "MSRP $78",
    name: "50mL Perfume - Bosque Místico",
    caseSize: "Case of 4",
    badge: "Bestseller",
  },
  {
    image:
      "https://cdn.faire.com/fastly/eb33a3aa2b11a16866287488726a6883e40ea9e1ff74b0c36b0d6c29cdc837cf.jpeg?format=jpg&precrop=1643%2C1643%2Cx294%2Cy246%2Csafe&width=720",
    price: "$25",
    msrp: "MSRP $50",
    name: "Todos Santos Candle",
    caseSize: "Case of 4",
    badge: "Bestseller",
  },
  {
    image:
      "https://cdn.faire.com/fastly/c3e78273bb628101c11e2f73659d329385985c1762bb6fc2472ffa088c4527d1.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
    price: "$25",
    msrp: "MSRP $50",
    name: "Merida Candle",
    caseSize: "Case of 4",
    badge: "Bestseller",
  },
  {
    image:
      "https://cdn.faire.com/fastly/5749ebcf3809e019dcd320adb4282ee6f526469a6bb9fca8bbcea980458e6d74.jpeg?format=jpg&precrop=2579%2C2579%2Cx313%2Cy225%2Csafe&width=720",
    price: "$7",
    msrp: "MSRP $14",
    name: "Flor De Mayo Cactus Soap",
    caseSize: "Case of 8",
    badge: "Bestseller",
  },
  {
    image:
      "https://cdn.faire.com/fastly/0cdc7b861b3f90a03f7825a3ae3228b8110e3db5c709cd3b97a2902605f56f83.jpeg?bg-color=FFFFFF&fit=crop&format=jpg&height=720&width=720",
    price: "$3.34",
    msrp: "MSRP $6.68",
    name: "6-Pack Mini 2oz Flor De Mayo Cactus Soap",
    caseSize: "Case of 6",
    badge: "Bestseller",
  },
  {
    image:
      "https://cdn.faire.com/fastly/94d214cac006424fc6225cded1155dea436e19474a69b76a2407a622207e37f9.jpeg?format=jpg&precrop=1:1&width=720",
    price: "$17",
    msrp: "MSRP $34",
    name: "Dulce De Cuerpo Cactus Body Cream - Deep Hydration",
    caseSize: "Case of 8",
  },
  {
    image:
      "https://cdn.faire.com/fastly/0790e3c407e45ccc7dfe66dacdc88c8356f1775f8c18cdbd70cf3d6cf94b1654.jpeg?format=jpg&precrop=824%2C824%2Cx169%2Cy253%2Csafe&width=720",
    price: "$16",
    msrp: "MSRP $32",
    name: "Tangerine Cactus Body Polish",
    caseSize: "Case of 8",
  },
];

interface Review {
  rating: number;
  date: string;
  text: string;
  author: string;
}

const RATING_BREAKDOWN: { stars: number; count: number }[] = [
  { stars: 5, count: 48 },
  { stars: 4, count: 1 },
  { stars: 3, count: 0 },
  { stars: 2, count: 0 },
  { stars: 1, count: 1 },
];

const REVIEWS: Review[] = [
  {
    rating: 5,
    date: "May 28, 2026",
    text: "Great products and communication!",
    author: "Caroline · Austin, TX, United States",
  },
  {
    rating: 5,
    date: "May 19, 2026",
    text: "Our customers are loving the connection to their cultura these scents create. Glad to be carrying this line and supporting a women-owned business.",
    author: "Noelle · Los Angeles, CA, United States",
  },
  {
    rating: 5,
    date: "May 5, 2026",
    text: "The scents are amazing. The communication was also amazing! I have purchased twice for my new shop opening and I will be back for more.",
    author: "Amber · Tarpon Springs, FL, United States",
  },
];

const TOTAL_REVIEWS = 50;

/**
 * Image section layout.
 * - `grid`: 2-column mosaic of 4 images (compact; pills sit high on the page).
 * - `stack`: full-width vertical stack of 2 large images (tall; pushes the
 *   inline assistant pills far below the fold — the case the FAB must solve).
 */
export type PdpImageLayout = "grid" | "stack";

export interface RetailerPdpProps {
  /**
   * Called when a retailer taps an "Your Assistant" prompt pill. Receives the
   * tapped question, or no argument for the generic "Ask something else" entry.
   * When omitted the pills are inert (standalone PDP with no docked panel).
   */
  onAskAssistant?: (question?: string) => void;
  /** Image section layout. Defaults to the 4-image `grid` mosaic. */
  imageLayout?: PdpImageLayout;
  /**
   * Attached to the inline "Ask your assistant" pill row so callers can observe
   * its visibility (e.g. to toggle a floating FAB when it scrolls out of view).
   */
  inlineAssistantRef?: React.Ref<HTMLDivElement>;
  /**
   * Replaces the "Top 10% reordered in Bath & body" line above the product
   * title. Lets a prototype own that badge's design (and make it navigate)
   * without forking the rail. Pass `null` to render nothing there. Omitted by
   * default, so existing prototypes render the production line exactly as
   * before.
   */
  badgeSlot?: React.ReactNode;
  /**
   * Replaces the "Up to 15% off" line below the price. Same semantics as
   * `badgeSlot`: `null` renders nothing, omitted keeps the production line.
   */
  promoBadgeSlot?: React.ReactNode;
  /**
   * Tightens the gap above the price block from 16px to 8px, for rail designs
   * that group headline, price and badges on a single 8px rhythm. Omitted
   * keeps production spacing.
   */
  tightPriceSpacing?: boolean;
  /**
   * Swaps the product identity — brand, imagery, title and pricing. Omitted
   * keeps the Nopalera product every existing prototype renders.
   */
  product?: RetailerPdpProductContent;
  /**
   * Replaces the hardcoded Details accordion after Description. Omitted keeps
   * the Nopalera SKU / origin / weight block.
   */
  attributeSections?: React.ReactNode;
  /**
   * Hides the production Description accordion so a prototype can render its
   * own (e.g. v1 Slate AccordionItem). Omitted keeps the production accordion.
   */
  hideDescriptionAccordion?: boolean;
  /**
   * Replaces the "Shop more from <brand>" section above Ratings & Reviews, so a
   * prototype can own the recommendation modules there. Omitted keeps the
   * production section.
   */
  miniShopSlot?: React.ReactNode;
  /**
   * Replaces the Compare products section below Ratings & Reviews. Omitted
   * keeps the production section.
   */
  belowReviewsSlot?: React.ReactNode;
}

/** The product this PDP has always shown; the default for `product`. */
export const NOPALERA_PDP_PRODUCT: RetailerPdpProductContent = {
  brandName: "Nopalera",
  brandAvatarUrl: BRAND_AVATAR,
  images: PRODUCT_IMAGES,
  name: "Lemongrass Cactus Soap",
  priceLabel: "$7.00",
  msrpLabel: "MSRP $14.00",
  caseSizeLabel: "Case of 8",
  quantityLabel: "8",
  cartTotalLabel: "Add to cart · $56.00",
};

const NOPALERA_DESCRIPTION = `Stimulate the senses and buff away bumps and buildup with our Revitalizing Cactus Soap. Powered by naturally exfoliating cactus fibers and infused with the refreshing scent of lemongrass, this luxurious take on traditional bar soap draws in moisture as it cleanses, quenching dryness and leaving skin softer, smoother, and visibly replenished.

Dermatologist-tested | Safe for sensitive skin`;

const NOPALERA_DETAILS = `SKU: PFS1120
Made in United States
Weight: 4 oz (113.4 g)
UPC: 860004684510
Production: Cruelty-free and Ethically sourced`;

const RetailerPdp: React.FC<RetailerPdpProps> = ({
  onAskAssistant,
  imageLayout = "grid",
  inlineAssistantRef,
  badgeSlot,
  promoBadgeSlot,
  tightPriceSpacing = false,
  product = NOPALERA_PDP_PRODUCT,
  attributeSections,
  hideDescriptionAccordion = false,
  miniShopSlot,
  belowReviewsSlot,
}) => (
  <div className="bg-fs-surface-primary min-h-screen">
    <RetailerTopNav />
    <BrandHeader product={product} />
    <div className="mx-auto w-full max-w-[1280px] px-4 md:px-12">
      <div className="flex flex-col gap-8 pt-4 lg:flex-row lg:items-start">
        <div className="flex w-full min-w-0 flex-col gap-6 lg:flex-1 lg:self-start">
          <ImageMosaic layout={imageLayout} product={product} />
          <AssistantPromptPills
            onAskAssistant={onAskAssistant}
            containerRef={inlineAssistantRef}
          />
        </div>
        <ProductDetailsRail
          onAskAssistant={onAskAssistant}
          badgeSlot={badgeSlot}
          promoBadgeSlot={promoBadgeSlot}
          tightPriceSpacing={tightPriceSpacing}
          product={product}
          attributeSections={attributeSections}
          hideDescriptionAccordion={hideDescriptionAccordion}
        />
      </div>
      {miniShopSlot === undefined ? (
        <ShopMoreSection brandName={product.brandName} />
      ) : (
        miniShopSlot
      )}
      <RatingsReviewsSection />
      {belowReviewsSlot === undefined ? (
        <div className="border-fs-border-muted border-t py-12">
          <CompareProducts retailerName="your store" />
        </div>
      ) : (
        belowReviewsSlot
      )}
    </div>
    <Footer />
  </div>
);

export default RetailerPdp;

const BrandHeader: React.FC<{ product: RetailerPdpProductContent }> = ({
  product,
}) => (
  <div className="bg-fs-surface-primary w-full">
    <div className="mx-auto w-full max-w-[1280px] px-4 py-4 md:px-12">
      <div className="flex min-h-12 items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            alt={product.brandName}
            className="h-10 w-10 rounded-full object-cover"
            src={product.brandAvatarUrl}
          />
          <div className="flex flex-col">
            <span className="type-fs-paragraph-medium text-fs-text-primary">
              {product.brandName}
            </span>
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-fs-text-primary flex items-center gap-1">
                <TopShopIcon />
                <span className="type-fs-paragraph-medium">Top Shop</span>
              </span>
              <span className="text-fs-text-subdued type-fs-paragraph-medium">
                ·
              </span>
              <StarIcon />
              <span className="type-fs-paragraph text-fs-text-primary">
                4.9 <span className="underline">(50)</span>
              </span>
            </div>
          </div>
        </div>
        <CartEntryPill />
      </div>
    </div>
  </div>
);

const CartEntryPill: React.FC = () => (
  <button className="border-fs-border-muted hover:border-fs-action-border-default flex h-12 items-center gap-2 rounded-full border pr-6 pl-2 transition-colors">
    <span className="flex h-8 items-center gap-1 rounded-full px-2">
      <CartIcon />
    </span>
    <span className="flex flex-col items-start justify-center">
      <span className="type-fs-label text-fs-text-primary whitespace-nowrap">
        $150 minimum
      </span>
      <span className="mt-1 h-1 w-24 overflow-hidden rounded-full bg-(--slate-color-grey-300)">
        <span className="bg-fs-action-surface-primary-default block h-full w-0" />
      </span>
    </span>
  </button>
);

const ImageMosaic: React.FC<{
  layout: PdpImageLayout;
  product: RetailerPdpProductContent;
}> = ({ layout, product }) => {
  const isStack = layout === "stack";
  const images = isStack ? product.images.slice(0, 2) : product.images;
  return (
    <div
      className={cn(
        "grid w-full gap-2",
        isStack ? "grid-cols-1" : "grid-cols-2"
      )}
    >
      {images.map((src, index) => (
        <div
          key={index}
          className="bg-fs-surface-primary aspect-square overflow-hidden brightness-[98%]"
        >
          <img
            alt={`${product.brandName} - ${product.name}`}
            className="h-full w-full cursor-zoom-in object-cover transition-transform duration-500 hover:scale-105"
            src={src}
          />
        </div>
      ))}
    </div>
  );
};

const ProductDetailsRail: React.FC<{
  onAskAssistant?: (question?: string) => void;
  badgeSlot?: React.ReactNode;
  promoBadgeSlot?: React.ReactNode;
  tightPriceSpacing?: boolean;
  product: RetailerPdpProductContent;
  attributeSections?: React.ReactNode;
  hideDescriptionAccordion?: boolean;
}> = ({
  onAskAssistant,
  badgeSlot,
  promoBadgeSlot,
  tightPriceSpacing,
  product,
  attributeSections,
  hideDescriptionAccordion = false,
}) => {
  const [withTester, setWithTester] = useState(false);

  return (
    <div className="flex w-full flex-col lg:w-[360px] lg:shrink-0">
      {badgeSlot === undefined ? (
        <p className="type-fs-paragraph-medium text-fs-message-text-sale">
          Top 10% reordered in Bath &amp; body
        </p>
      ) : (
        badgeSlot
      )}

      {/* Name + actions */}
      <div className="mt-1 flex items-start gap-4">
        <h1 className="type-fs-section-heading text-fs-text-primary flex-1">
          {product.name}
        </h1>
        <div className="flex items-center gap-2">
          <button aria-label="Save product to board" className="p-1">
            <HeartIcon />
          </button>
          <button aria-label="Share" className="p-1">
            <ShareIcon />
          </button>
        </div>
      </div>

      <div
        className={cn(
          "flex items-baseline gap-2",
          tightPriceSpacing ? "mt-2" : "mt-4"
        )}
      >
        <span className="type-fs-subheading-medium text-fs-text-primary">
          {product.priceLabel}
        </span>
        <span className="type-fs-paragraph text-fs-text-primary">
          {product.msrpLabel}
        </span>
      </div>
      {promoBadgeSlot === undefined ? (
        <p className="type-fs-paragraph text-fs-message-text-sale mt-1">
          Up to 15% off
        </p>
      ) : (
        promoBadgeSlot
      )}

      {/* Tester */}
      <label className="mt-6 flex cursor-pointer items-center gap-2">
        <input
          type="checkbox"
          checked={withTester}
          onChange={(event) => setWithTester(event.target.checked)}
          className="size-4 accent-(--slate-color-grey-900)"
        />
        <span className="type-fs-paragraph text-fs-text-primary">
          Include a tester for $3.50
        </span>
      </label>
      <p className="type-fs-label text-fs-text-subdued mt-1 ml-6">
        Tester items are not refundable
      </p>

      {/* Quantity */}
      <div className="mt-6">
        <div className="flex items-center justify-between">
          <span className="type-fs-paragraph text-fs-text-primary">
            Item Quantity
          </span>
          <span className="type-fs-paragraph text-fs-text-primary">
            {product.caseSizeLabel}
          </span>
        </div>
        <div className="border-fs-border-muted mt-1 flex h-12 items-center justify-between rounded-md border px-3">
          <span className="type-fs-paragraph text-fs-text-primary">
            {product.quantityLabel}
          </span>
          <ChevronIcon className="text-fs-icon-primary size-4 rotate-90" />
        </div>
      </div>

      <div className="mt-4">
        <Button variant="primary" size="medium" fullWidth>
          {product.cartTotalLabel}
        </Button>
      </div>

      {/* Insider promo */}
      <div className="mt-6 flex items-start gap-2 rounded-lg bg-[#f2f5f5] p-4">
        <img
          alt=""
          aria-hidden="true"
          className="relative -bottom-1 size-[18px] shrink-0"
          src="https://cdn.faire.com/fastly/ca10aa727759c22347f57b72104cc01a46d331349ba4e3647540f6fcf5bf07e5.png"
        />
        <p className="type-fs-paragraph text-fs-text-primary">
          <b style={{ color: "#36676A" }}>Free shipping over $300</b> with this
          brand. Only with an Insider membership.{" "}
          <button className="text-fs-action-text-default underline">
            Learn more
          </button>
        </p>
      </div>

      {/* Shipping & policies */}
      <div className="mt-6 flex flex-col gap-3">
        <h2 className="type-fs-paragraph-medium text-fs-text-primary">
          Shipping &amp; policies
        </h2>
        <ShippingRow icon={<CalendarIcon />}>
          Estimated delivery{" "}
          <span className="underline decoration-dotted">Jun 21-Jul 1</span>
        </ShippingRow>
        <ShippingRow icon={<GlobeIcon />}>Ships from United States</ShippingRow>
        <ShippingRow icon={<ReturnIcon />}>
          Eligible for{" "}
          <span className="underline decoration-dotted">free returns</span>
        </ShippingRow>
      </div>

      <hr className="border-fs-border-muted mt-6" />
      {hideDescriptionAccordion ? null : (
        <Accordion title="Product description" clampLines={3}>
          <p className="type-fs-paragraph text-fs-text-primary whitespace-pre-line">
            {product.description ?? NOPALERA_DESCRIPTION}
          </p>
        </Accordion>
      )}
      {attributeSections !== undefined ? (
        attributeSections
      ) : (
        <>
          <hr className="border-fs-border-muted" />
          <Accordion title="Details" clampLines={2}>
            <p className="type-fs-paragraph text-fs-text-primary whitespace-pre-line">
              {NOPALERA_DETAILS}
            </p>
          </Accordion>
          <hr className="border-fs-border-muted" />
        </>
      )}
    </div>
  );
};

const ShippingRow: React.FC<{
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, children }) => (
  <div className="flex items-start gap-2">
    <span className="text-fs-text-primary mt-0.5 shrink-0">{icon}</span>
    <p className="type-fs-paragraph text-fs-text-primary">{children}</p>
  </div>
);

const Accordion: React.FC<{
  title: string;
  clampLines?: number;
  children: React.ReactNode;
}> = ({ title, clampLines = 3, children }) => {
  const [open, setOpen] = useState(false);
  const clampClass =
    clampLines === 2 ? "line-clamp-2" : clampLines === 3 ? "line-clamp-3" : "";
  return (
    <div className="py-4">
      <button
        className="flex w-full items-start justify-between gap-6 text-left"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <div className="flex flex-1 flex-col gap-2">
          <h2 className="type-fs-paragraph-medium text-fs-text-primary">
            {title}
          </h2>
          <div className={cn(!open && clampClass)}>{children}</div>
        </div>
        <DownChevronIcon
          className={cn(
            "text-fs-icon-primary mt-1 size-4 shrink-0",
            open && "rotate-180"
          )}
        />
      </button>
    </div>
  );
};

const AssistantPromptPills: React.FC<{
  onAskAssistant?: (question?: string) => void;
  containerRef?: React.Ref<HTMLDivElement>;
}> = ({ onAskAssistant, containerRef }) => (
  <div
    ref={containerRef}
    data-test-id="pdpAssistantInlineSection"
    className="flex w-full max-w-full min-w-0 flex-wrap items-center gap-(--slate-dimensions-2)"
    style={
      { "--pdp-assistant-pill-hover-border": "#D7A3FF" } as React.CSSProperties
    }
  >
    <AskAssistantPill nudgeOnEnter onClick={() => onAskAssistant?.()} />
    {ASSISTANT_PROMPTS.map((prompt) => (
      <button
        key={prompt}
        type="button"
        aria-label={prompt}
        onClick={() => onAskAssistant?.(prompt)}
        className={PROMPT_PILL_CLASS}
      >
        <span className="block min-w-0 truncate">{prompt}</span>
      </button>
    ))}
  </div>
);

const ShopMoreSection: React.FC<{ brandName: string }> = ({ brandName }) => (
  <section className="pt-12 pb-8">
    <div className="flex flex-col justify-between gap-4 pb-6 lg:flex-row lg:items-center">
      <div className="flex items-center gap-3">
        <span
          className="grid h-12 w-12 rounded-full border-2 border-solid"
          style={{ borderColor: "rgb(139, 148, 120)" }}
        >
          <img
            alt="Nopalera"
            className="h-full w-full overflow-hidden rounded-full object-cover"
            src={BRAND_AVATAR}
          />
        </span>
        <div className="flex flex-col gap-1">
          <h2 className="type-fs-subheading-medium text-fs-text-primary !no-underline">
            {`Shop more from ${brandName}`}
          </h2>
          <div className="text-fs-text-primary flex items-center gap-1">
            <TopShopIcon />
            <span className="type-fs-paragraph">Top Shop</span>
            <span className="type-fs-paragraph underline">
              • San Antonio, Texas
            </span>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button variant="primary" size="medium">
          Shop all 38 products
        </Button>
        <Button
          variant="secondary"
          size="medium"
          iconProps={{ Component: MessageIcon, position: "start" }}
        >
          Message brand
        </Button>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-5">
      {SHOP_MORE_PRODUCTS.map((product) => (
        <ProductTile key={product.name} product={product} />
      ))}
    </div>
  </section>
);

const ProductTile: React.FC<{ product: ShopProduct }> = ({ product }) => (
  <div className="flex flex-col">
    <div className="relative">
      <img
        alt={product.name}
        className="rounded-fs-component-default aspect-square w-full object-cover"
        src={product.image}
      />
      {product.badge ? (
        <div className="border-fs-border-muted/70 bg-fs-surface-primary type-fs-label-medium text-fs-text-primary absolute top-2 left-2 inline-flex min-h-[22px] items-center rounded border border-solid px-[7px] py-[3px]">
          {product.badge}
        </div>
      ) : null}
      <button
        aria-label="Save product to board"
        className="absolute top-2 right-2"
      >
        <DarkHeartIcon />
      </button>
      <button
        aria-label="Quick add"
        className="border-fs-border-muted bg-fs-surface-primary text-fs-text-active hover:border-fs-action-border-active hover:bg-fs-surface-secondary absolute right-2 bottom-2 flex size-8 items-center justify-center rounded-2xl border transition-all"
      >
        <PlusIcon />
      </button>
    </div>
    <div className="mt-2 flex flex-col gap-0.5">
      <div className="flex flex-wrap items-baseline gap-x-1">
        <span className="type-fs-subheading-medium text-fs-text-primary">
          {product.price}
        </span>
        <span className="type-fs-label text-fs-text-primary">
          {product.msrp}
        </span>
      </div>
      <p className="type-fs-paragraph-medium text-fs-text-primary m-0 line-clamp-2 break-words">
        {product.name}
      </p>
      <p className="type-fs-paragraph text-fs-text-subdued m-0">
        {product.caseSize}
      </p>
    </div>
  </div>
);

const RatingsReviewsSection: React.FC = () => {
  const maxCount = Math.max(...RATING_BREAKDOWN.map((row) => row.count), 1);
  return (
    <section className="border-fs-border-muted border-t py-12">
      <div className="flex flex-col gap-6 lg:flex-row lg:gap-[60px]">
        <div className="lg:w-[244px] lg:shrink-0">
          <h2 className="type-fs-page-heading text-fs-text-primary m-0">
            Ratings &amp; Reviews
          </h2>
          <div className="mt-5 flex flex-col gap-1">
            <div className="flex items-center gap-1">
              <span className="type-fs-display-s-sans">4.9</span>
              <StarIcon />
            </div>
            <p className="type-fs-paragraph text-fs-text-primary m-0">
              Rating (
              <span className="underline">{TOTAL_REVIEWS} brand reviews</span>)
            </p>
          </div>
          <div className="mt-5 flex flex-col gap-1">
            {RATING_BREAKDOWN.map((row) => (
              <div key={row.stars} className="flex h-3 items-center gap-3">
                <span className="type-fs-label w-2.5 text-right leading-none">
                  {row.stars}
                </span>
                <div className="w-[187px]">
                  <div
                    className="relative flex-1 rounded-[3px]"
                    style={{
                      backgroundColor: "var(--slate-color-grey-400)",
                      height: 4,
                    }}
                  >
                    <div
                      className="absolute top-0 left-0 h-full rounded-[3px]"
                      style={{
                        backgroundColor:
                          "var(--slate-color-surface-primary-inverse, #333333)",
                        width: `${(row.count / maxCount) * 100}%`,
                      }}
                    />
                  </div>
                </div>
                <span className="type-fs-label text-fs-text-subdued w-6 text-left leading-none">
                  {row.count}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative min-w-0 flex-1">
          <div className="mb-2 flex items-center justify-end gap-4">
            <button className="type-fs-paragraph text-fs-text-primary underline">
              See all reviews
            </button>
            <div className="flex gap-2">
              <span className="border-fs-border-muted flex size-7 items-center justify-center rounded-full border">
                <CarouselArrow direction="left" />
              </span>
              <span className="border-fs-border-muted flex size-7 items-center justify-center rounded-full border">
                <CarouselArrow direction="right" />
              </span>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {REVIEWS.map((review) => (
              <ReviewCard key={review.author} review={review} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ReviewCard: React.FC<{ review: Review }> = ({ review }) => (
  <div className="border-fs-border-muted flex h-full min-h-39 flex-col gap-3 rounded-[6px] border px-4 py-6">
    <div className="flex items-center justify-between gap-2">
      <StarRow count={review.rating} />
      <span className="type-fs-label text-fs-text-subdued shrink-0">
        {review.date}
      </span>
    </div>
    <div className="flex min-h-0 flex-1 flex-col gap-1">
      <p className="type-fs-paragraph m-0 line-clamp-4">{review.text}</p>
      <p className="type-fs-label text-fs-text-subdued m-0 mt-auto">
        {review.author}
      </p>
    </div>
  </div>
);

const StarRow: React.FC<{ count: number }> = ({ count }) => (
  <div className="flex" role="img" aria-label={`${count} of 5 stars`}>
    {Array.from({ length: count }).map((_, index) => (
      <StarFilledIcon key={index} />
    ))}
  </div>
);

// ── Icons ────────────────────────────────────────────────────────────────

const TopShopIcon: React.FC = () => (
  <svg
    viewBox="0 0 12 16"
    className="h-3 w-3"
    fill="none"
    aria-hidden="true"
    style={{ position: "relative", top: "0.65px" }}
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.577 11.57a5.43 5.43 0 0 0 1.645-3.888V.254H0v7.428c0 1.457.584 2.859 1.64 3.888.759.741 1.71 1.25 2.737 1.477-1.194.362-2.132 1.236-2.507 2.34h7.481c-.375-1.1-1.317-1.977-2.511-2.34a5.65 5.65 0 0 0 2.737-1.477"
      fill="currentColor"
    />
  </svg>
);

const StarIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-3"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z" />
  </svg>
);

const CartIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M20.74 14.714H7.744L6.029 5.286h16.114a.857.857 0 0 1 .843 1.01l-1.402 7.714a.86.86 0 0 1-.843.704ZM6.029 5.286l-.72-3.6A.86.86 0 0 0 4.469 1H1.743M7.743 14.714l.72 3.6a.86.86 0 0 0 .84.686h9.583" />
    <path d="M18.884 22.429a.857.857 0 1 1-1.714 0 .857.857 0 0 1 1.714 0ZM11.17 22.429a.857.857 0 1 1-1.715 0 .857.857 0 0 1 1.715 0Z" />
  </svg>
);

const HeartIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21.343 2.539 12.69C-2.571 7.58 4.949-2.33 12 5.662c7.05-7.99 14.572 1.918 9.461 7.029z" />
  </svg>
);

const ShareIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M17.75 8.714h1.643a.82.82 0 0 1 .821.822v12.321a.82.82 0 0 1-.821.822H4.607a.82.82 0 0 1-.821-.822V9.536a.82.82 0 0 1 .821-.822H6.25M12 12.822v-11.5m3.286 3.285L12 1.322 8.714 4.607" />
  </svg>
);

const ChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M6.607.857 17.143 11.39c.16.16.252.376.252.61s-.092.45-.252.61L6.607 23.143" />
  </svg>
);

const DownChevronIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const CalendarIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M2.571 4.285A1.714 1.714 0 0 0 .857 6v15.428a1.714 1.714 0 0 0 1.714 1.714h18.857a1.714 1.714 0 0 0 1.715-1.714V6a1.714 1.714 0 0 0-1.715-1.715H18M.857 11.143h22.286M6 .858v6.857M18 .858v6.857M6 4.285h8.571" />
  </svg>
);

const GlobeIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M23.143 12A11.143 11.143 0 1 0 12 23.143M.857 12h22.286M12 23.143A11.143 11.143 0 0 0 23.143 12M12 23.143A19.24 19.24 0 0 0 16.285 12 19.24 19.24 0 0 0 12 .857m0 22.286A19.24 19.24 0 0 1 7.714 12 19.24 19.24 0 0 1 12 .857" />
  </svg>
);

const ReturnIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m7.8 11.8 3 3 6-6m6.343 9.2-.857-3.429-3.429.858m3.429-.858A11.31 11.31 0 0 1 12 22.286a10.32 10.32 0 0 1-9.663-6.772M.857 6l.857 3.429 3.429-.858m-3.429.858A11.63 11.63 0 0 1 12 1.714a10.32 10.32 0 0 1 9.664 6.772" />
  </svg>
);

const MessageIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M11.997.923A11.07 11.07 0 0 0 2.239 6.77a11.08 11.08 0 0 0 .541 11.365L.923 23.077l6.218-1.125a11.072 11.072 0 0 0 15.652-7.46A11.08 11.08 0 0 0 16.81 2.022 11.1 11.1 0 0 0 11.997.923Z" />
  </svg>
);

const DarkHeartIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-5"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 21.343 2.539 12.69C-2.571 7.58 4.949-2.33 12 5.662c7.05-7.99 14.572 1.918 9.461 7.029z" />
  </svg>
);

const PlusIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-4"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.25"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 1.714v20.572M1.714 12h20.572" />
  </svg>
);

const CarouselArrow: React.FC<{ direction: "left" | "right" }> = ({
  direction,
}) => (
  <svg
    viewBox="0 0 24 24"
    className="size-4"
    fill="none"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {direction === "left" ? (
      <path d="m16 3-8.79 8.51A.7.7 0 0 0 7 12a.67.67 0 0 0 .21.49L16 21" />
    ) : (
      <path d="m9 3 8.79 8.51A.7.7 0 0 1 18 12a.67.67 0 0 1-.21.49L9 21" />
    )}
  </svg>
);

const StarFilledIcon: React.FC = () => (
  <svg
    viewBox="0 0 24 24"
    className="size-4"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="m12.844 1.87 2.713 5.501a.94.94 0 0 0 .708.515l6.072.884a.94.94 0 0 1 .515 1.603l-4.396 4.286a.94.94 0 0 0-.269.831l1.037 6.055a.94.94 0 0 1-1.371.99l-5.416-2.848a.94.94 0 0 0-.874 0l-5.43 2.856a.94.94 0 0 1-1.37-.99L5.8 15.5a.94.94 0 0 0-.27-.832l-4.388-4.289a.939.939 0 0 1 .514-1.603l6.079-.89a.94.94 0 0 0 .708-.514l2.714-5.5a.941.941 0 0 1 1.687 0Z" />
  </svg>
);
